/**
 * Sockets (docs/SKEL-STORY.md Q4): the point on a posed puppet where a held prop sits.
 *
 * A socket is a bone plus an offset in that bone's own frame (world px, y up) — the same frame
 * `construct.boneToWorld` maps from, so the offset turns and scales with the bone. The result is in
 * the root's world space (y up), like every other `construct` output; the player converts to stage
 * pixels with the view's `k` and floor line.
 */
import { restPose } from './animate'
import type { Pose } from './animate'
import { boneToWorld, construct } from './construct'
import type { WorldBone } from './construct'
import type { SkfRoot, V2 } from './types'

export interface SocketDef {
  bone: string
  /** Bone-local offset: `[x, y]` or `{x, y}`. Absent = the bone's origin. */
  offset?: readonly [number, number] | V2 | null
}

export interface SocketPoint {
  x: number
  y: number
  /** World rotation of the socket bone (radians, y up). */
  angle: number
  /** Index of the socket bone in `root.bones`. */
  bone: number
}

/** Bone index by name: exact first, then case-insensitive. −1 when absent. */
export function boneIndexByName(root: SkfRoot, name: string): number {
  const exact = root.bones.findIndex((b) => b.name === name)
  if (exact >= 0) return exact
  const want = name.toLowerCase()
  return root.bones.findIndex((b) => b.name.toLowerCase() === want)
}

function offsetOf(o: SocketDef['offset']): V2 {
  if (!o) return { x: 0, y: 0 }
  if (Array.isArray(o)) {
    const [x, y] = o as readonly [number, number]
    return { x: Number.isFinite(x) ? x : 0, y: Number.isFinite(y) ? y : 0 }
  }
  const v = o as V2
  return { x: Number.isFinite(v.x) ? v.x : 0, y: Number.isFinite(v.y) ? v.y : 0 }
}

/**
 * The world point of `socket` on `root` posed by `pose` (rest when null). Pass `world` when the
 * caller already constructed this pose — it is then used as is. Null when the bone does not exist.
 */
export function socketPoint(root: SkfRoot, pose: Pose | null, world: readonly WorldBone[] | null, socket: SocketDef): SocketPoint | null {
  const id = boneIndexByName(root, socket.bone)
  if (id < 0) return null
  const w = world ?? construct(root, pose ?? restPose(root))
  const b = w[id]
  if (!b) return null
  const p = boneToWorld(b, offsetOf(socket.offset))
  return { x: p.x, y: p.y, angle: b.rot, bone: id }
}
